import { Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { SETTLE_SPRING } from "./SectionHeading";

/**
 * A slowly rotating circular-text stamp with a claw asterisk at its center.
 * Decorative; the tagline it spells is carried by the visible copy around it.
 */
function SpinStamp({ text }: { text: string }) {
  return (
    <div aria-hidden="true" className="relative h-32 w-32">
      <svg
        viewBox="0 0 100 100"
        className="h-full w-full animate-[spin_28s_linear_infinite] motion-reduce:animate-none"
      >
        <defs>
          <path
            id="final-stamp-circle"
            d="M50,50 m-40,0 a40,40 0 1,1 80,0 a40,40 0 1,1 -80,0"
          />
        </defs>
        <text
          fontFamily="var(--font-body, 'General Sans', sans-serif)"
          fontSize="10"
          fontWeight="700"
          letterSpacing="1.5"
          fill="#211F1C"
        >
          <textPath href="#final-stamp-circle" textLength="250" lengthAdjust="spacingAndGlyphs">
            {text}
          </textPath>
        </text>
      </svg>

      {/* Claw asterisk: three crossed strokes, one pinched like a claw */}
      <svg viewBox="0 0 48 48" className="absolute inset-0 m-auto h-12 w-12">
        <g stroke="#211F1C" strokeWidth="5" strokeLinecap="round">
          <line x1="24" y1="8" x2="24" y2="40" />
          <line x1="10" y1="16" x2="38" y2="32" />
          <line x1="38" y1="16" x2="10" y2="32" />
        </g>
        <circle cx="24" cy="24" r="5.5" fill="#FBD34C" stroke="#211F1C" strokeWidth="3" />
      </svg>
    </div>
  );
}

export function FinalCtaSection() {
  const { t } = useTranslation("home");

  return (
    <section className="overflow-hidden px-5 py-20 md:py-28">
      {/* One big solid-yellow ellipse, Timeleft-quiz style */}
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 24 }}
        whileInView={{ opacity: 1, scale: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={SETTLE_SPRING}
        className="mx-auto flex w-full max-w-5xl flex-col items-center justify-center rounded-[48px] bg-home-yellow px-8 py-16 text-center md:min-h-[520px] md:rounded-[50%] md:px-28 md:py-24"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.5, rotate: -30 }}
          whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ type: "spring", stiffness: 170, damping: 13, delay: 0.1 }}
          className="mb-6"
        >
          <SpinStamp text={t("finalCta.stamp")} />
        </motion.div>

        <h2 className="max-w-2xl font-display text-[clamp(30px,4.2vw,54px)] leading-[1.05] font-semibold tracking-tight text-home-ink">
          {t("finalCta.title")}
        </h2>

        <p className="mx-auto mt-5 max-w-md font-body text-[17px] leading-relaxed text-home-ink/70">
          {t("finalCta.body")}
        </p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ ...SETTLE_SPRING, delay: 0.2 }}
          className="mt-9 w-full max-w-sm"
        >
          <Link
            to="/login"
            search={{ pendingCode: undefined }}
            className="block w-full rounded-full bg-white px-9 py-4 text-center font-body text-base font-bold text-home-ink shadow-[0_14px_34px_-14px_rgba(33,31,28,0.5)] transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-14px_rgba(33,31,28,0.55)]"
          >
            {t("finalCta.cta")}
          </Link>
        </motion.div>

        {/* /75 not /65 here: the yellow surface is brighter than cream, so the
            shared muted token lands at 4.42 — just under AA for 13px. */}
        <p className="mt-5 font-body text-[13px] font-medium tracking-wide text-home-ink/75">
          {t("finalCta.trust")}
        </p>
      </motion.div>
    </section>
  );
}
